import {
  isMobile,
  browserName,
  osName,
  osVersion,
  deviceType,
  browserVersion,
} from 'react-device-detect';
import { matchRoutes } from 'react-router-dom';
import {
  VITE_PROFILE_CONFIG,
  VITE_CLIENT_MODE,
  VITE_SPLUNK_RUM_AUTH,
  VITE_SPLUNK_RUM_ENV,
} from './constants/envConstants';
import { store } from '../store/store';
import { setError } from '../store/appStateSlice';
import { setLanguageContent } from '../store/contentSlice';
import {
  CONTROL_CENTER_IMAGE_PATH,
  CONTROL_CENTER_DEFAULT_FEATURE_FLAGS,
} from './constants/controlCenterConstants';
import {
  CLIENT_ID_FULL_PARAM_KEY,
  ORIGIN_CHANNEL_PARAM_KEY,
  CLIENT_ID_SHORT_PARAM_KEY,
  STRATEGY_PARAM_KEY,
  LANGUAGE_PARAM_KEY,
  SITE_CODE_PARAM_KEY,
  D2D_TOKEN_PARAM_KEY,
} from './constants/queryParamConstants';
import {
  ORIGIN_SESSION_STORAGE_KEY,
  CLIENT_ID_SESSION_STORAGE_KEY,
  D2D_TOKEN_SESSION_STORAGE_KEY,
  AUTH_TOKEN_SESSION_STORAGE_KEY,
  SESSION_ID_SESSION_STORAGE_KEY,
  EXCL_ACCOUNT_SESSION_STORAGE_KEY,
  LANGUAGE_SESSION_STORAGE_KEY,
  SITE_CODE_SESSION_STORAGE_KEY,
  STRATEGY_SESSION_STORAGE_KEY,
} from './constants/sessionStorageConstants';
import {
  ALLOWED_ORIGIN_VALUES,
  APP_ORIGIN_D2D,
} from './constants/originConstants';
import {
  CLIENT_ID_PAYSOL,
  DATE_FORMAT_MONTHS_LIST_EN,
  DATE_FORMAT_MONTHS_LIST_ES,
} from './constants/genericConstants';
import { validateInputParams } from '../interfaces/helpers.interface';

// returns if the env is local or not
export const isLocal = (): boolean => VITE_PROFILE_CONFIG === 'local';

// returns if mock mode is enabled or not
export const isMock = (): boolean => VITE_CLIENT_MODE === 'mock';

export const parseJson = (data: string): object => {
  let parsedData = {};
  try {
    parsedData = JSON.parse(data);
  } catch (error) {
    parsedData = {};
    console.log(
      `Error in parsing ${data} and here is the error log:::::`,
      error,
    );
  }
  return parsedData;
};

// checks if the object passed is empty or not
export const isEmpty = (param: object): boolean =>
  Object.keys(param).length === 0 && param.constructor === Object;

export const getBrowserDetails = (): string =>
  `browserName:${browserName},browserVersion:${browserVersion}`;

export const getDeviceDetails = (): string =>
  `deviceType:${deviceType},osName:${osName},osVersion${osVersion}`;

export const detectDeviceType = isMobile ? 'mobile' : 'desktop';

export const getDeviceFingerPrint = (): string =>
  window.ioGetBlackbox().blackbox;

export const getParamValue = (key: string): string => {
  const params = new URL(document.location.toString()).searchParams;
  let value = params.get(key);
  if (!value) {
    value = sessionStorage.getItem(key) || '';
  }
  return value;
};

export const getClientIdParamValue = (): string =>
  getParamValue(CLIENT_ID_FULL_PARAM_KEY) ||
  getParamValue(CLIENT_ID_SHORT_PARAM_KEY) ||
  store.getState().session.clientId;

export const setUpSessionStorage = (): boolean => {
  sessionStorage.clear();
  // retrieve query params
  const clientParamValue = getClientIdParamValue();
  const d2dTokenParamValue = getParamValue(D2D_TOKEN_PARAM_KEY);
  const originChannelValue = getParamValue(ORIGIN_CHANNEL_PARAM_KEY);
  const strategyParamValue = getParamValue(STRATEGY_PARAM_KEY); // eg: ?r=ihs
  const exclAccountValue =
    getParamValue(EXCL_ACCOUNT_SESSION_STORAGE_KEY) || 'n';
  const isOriginValid = ALLOWED_ORIGIN_VALUES.includes(originChannelValue);
  const languageValue = getParamValue(LANGUAGE_PARAM_KEY) || 'en';
  const siteCodeValue = getParamValue(SITE_CODE_PARAM_KEY);
  store.dispatch(setLanguageContent(languageValue.toLocaleUpperCase()));

  // save values to session storage
  sessionStorage.setItem(CLIENT_ID_SESSION_STORAGE_KEY, clientParamValue);
  sessionStorage.setItem(D2D_TOKEN_SESSION_STORAGE_KEY, d2dTokenParamValue);
  sessionStorage.setItem(STRATEGY_SESSION_STORAGE_KEY, strategyParamValue);
  sessionStorage.setItem(EXCL_ACCOUNT_SESSION_STORAGE_KEY, exclAccountValue);
  sessionStorage.setItem(LANGUAGE_SESSION_STORAGE_KEY, languageValue);
  sessionStorage.setItem(SITE_CODE_SESSION_STORAGE_KEY, siteCodeValue);

  if (isOriginValid) {
    sessionStorage.setItem(ORIGIN_SESSION_STORAGE_KEY, originChannelValue);
    return true;
  } else if (d2dTokenParamValue) {
    // origin is 'd2d' of d2dToken is present
    sessionStorage.setItem(ORIGIN_SESSION_STORAGE_KEY, APP_ORIGIN_D2D);
    return true;
  } else {
    // origin is set 'dl' aka 'direct-link' as default
    sessionStorage.setItem(ORIGIN_SESSION_STORAGE_KEY, 'dl');
    return false;
  }
};

export const mapOriginQueryParam = (originChannelValue: string): string => {
  switch (originChannelValue) {
    case 'qr':
      return 'qr-code';
    case 'dl':
      return 'direct-link';
    case 'el':
      return 'email-link';
    case 'sms':
      return 'sms';
    case 'dl_iva':
      return 'direct-link_iva';
    case 'sms_iva':
      return 'sms_iva';
    case 'dl_wks':
      return 'direct-link_workstation';
    case 'sms_wks':
      return 'sms_workstation';
    default:
      return 'direct-link';
  }
};

export const getAuthTokenFromSession = (): string | null =>
  sessionStorage.getItem(AUTH_TOKEN_SESSION_STORAGE_KEY);

export const getClientIdFromSession = (): string | null =>
  sessionStorage.getItem(CLIENT_ID_SESSION_STORAGE_KEY);

export const getSessionIdFromSession = (): string | null =>
  sessionStorage.getItem(SESSION_ID_SESSION_STORAGE_KEY);

/**
 * @description to calculate difference between 2 dates is less than 15 days or not
 * @param dateA string in "yyyy-mm-dd" format
 * @param dateB string in ""yyyy-mm-dd" format
 * @returns string in true if less than 15 days otherwise false
 */
export const isDateDifferenceLessThan15Days = (
  dateA: string,
  dateB: string,
): boolean => {
  const MIL_SECS_PER_DAY = 24 * 60 * 60 * 1000;
  const dateObjA = new Date(dateA).getTime();
  const dateObjB = new Date(dateB).getTime();

  if (isNaN(dateObjA) || isNaN(dateObjB)) {
    console.log('Invalid date format.');
    return false;
  } else {
    const differenceInMilSecs = dateObjA - dateObjB;
    const differenceInDays = Math.abs(differenceInMilSecs) / MIL_SECS_PER_DAY;
    return differenceInDays <= 15;
  }
};

/**
 * @description to format date
 * @param dateToFormat string in ""yyyy-mm-dd" format
 * @returns string in "May 8, 2024" format
 */
export const showDateInFormat = (dateToFormat: string) => {
  if (!dateToFormat) return 'NA';
  const [year, month, day] = dateToFormat.split('-');
  const dayWithoutLeadingZero = day.startsWith('0') ? day.slice(1) : day;
  const getLang: any = sessionStorage.getItem(LANGUAGE_SESSION_STORAGE_KEY);
  const monthNames =
    convertStringToLowerCase(getLang) === 'es'
      ? DATE_FORMAT_MONTHS_LIST_ES
      : DATE_FORMAT_MONTHS_LIST_EN;
  return `${monthNames[+month - 1]} ${dayWithoutLeadingZero}, ${year}`;
};

/**
 * @description used to display/hide the profile menu/user icon on header based on the route
 * @returns true/false if the user menu should be visible on the route.
 * Any routes not part of this will not show the caret icon or profile menu
 */
export const isRouteBehindLogin = (): boolean => {
  const routesBehindLogin = [{ path: '/dashboard' }];
  const currentLocation = window.location.pathname;
  const routesMatch = matchRoutes(routesBehindLogin, currentLocation) ?? [];

  return !!routesMatch && routesMatch.length > 0;
};

/**
 * @description filterAccountsByAllowedFeatures allows features/accounts to be shown based on feature flags from accounts API and control center API
 * @param accounts array of accounts objects. Each account has features object which has boolean flags from "features API"
 * @param controlCenterClientFeatures array of objects which has client based feature flags from "control center API"
 * @returns array of accounts objects. Features are updated as per both passed on API values
 */
export const filterAccountsByAllowedFeatures = (
  accounts: any,
  controlCenterClientFeatures: any,
) => {
  // updatedAccounts - final array with updated feature flags in accounts response
  // account - individual account object inside accounts array
  return accounts.reduce((updatedAccounts: any, account: any) => {
    // controlCenterClientFeature - found features from control center for current client from accounts
    const controlCenterClientFeature = controlCenterClientFeatures.find(
      (clientConfig: any) => {
        // making sure all values are valid before converting case
        const accountClientId = convertStringToLowerCase(account.clientId);
        const accountPortfolio = convertStringToLowerCase(account.portfolio);
        const configClientId = convertStringToLowerCase(clientConfig.client);
        /* incase the client id is 'paysol' aka one of Payment solutions portfolios,
        / which is an umbrella value, compare the portfolio name to features.client */
        if (accountClientId === CLIENT_ID_PAYSOL) {
          return configClientId === accountPortfolio;
        } else {
          return configClientId === accountClientId;
        }
      },
    ) || {
      features: CONTROL_CENTER_DEFAULT_FEATURE_FLAGS, // default values for flags incase no response from control center API is received
    };

    // push the new values for each feature flag after comparing both API responses on every iteration of accounts
    updatedAccounts.push({
      ...account,
      features: {
        // for flags exclusive to control center, keep the values unchanged
        ...controlCenterClientFeature.features,
        // for flags common in control center and account features do below
        // if both API flags are true then sets the flag to true otherwise false
        accessToAppleWallet:
          controlCenterClientFeature.features.accessToAppleWallet &&
          account.features.accessToAppleWallet,
        accessToGoogleWallet:
          controlCenterClientFeature.features.accessToGoogleWallet &&
          account.features.accessToGoogleWallet,
        accessToSamsungWallet:
          controlCenterClientFeature.features.accessToSamsungWallet &&
          account.features.accessToSamsungWallet,
        accessToVirtualCard:
          controlCenterClientFeature.features.accessToVirtualCard &&
          account.features.accessToVirtualCard,
        accessToSingleUseCard:
          controlCenterClientFeature.features.accessToSingleUseCard &&
          account.features.accessToSingleUseCard,
        accessToTempShoppingPass:
          controlCenterClientFeature.features.accessToTempShoppingPass &&
          account.features.accessToTempShoppingPass,
        accessToTokenizedShoppingPass:
          controlCenterClientFeature.features.accessToTokenizedShoppingPass &&
          account.features.accessToTokenizedShoppingPass,
        accessToPlasticId:
          controlCenterClientFeature.features.accessToPlasticId &&
          account.features.accessToPlasticId,
      },
    });

    return updatedAccounts;
  }, []);
};

export const filterAccountsWithNoFeatures = (updateAndFilteredAccounts: any) =>
  updateAndFilteredAccounts.filter((account: any) => {
    const { features } = account;
    // return if any one of these flags are true
    return Object.values(features).some((value) => value === true);
  });

export const getAccountsSorted = (accounts: Array<object>) => {
  /** step-1: Separate the leadAccounts and other account in
   * leadAccounts and restOfTheAccounts respectively.
   *
   */
  const leadAccounts: Array<object> = [];
  const restOfTheAccounts: Array<object> = [];
  let finalAccountList: any;
  accounts.forEach((value: any) => {
    if (value.isLeadAccount) {
      leadAccounts.push(value);
    } else {
      restOfTheAccounts.push(value);
    }
  });

  /** step-2: restOfTheAccounts is the accounts sorting in alphabetical order, in respective of clientid.
   * e.g. [{b},{a},{d},{c}] to [{a},{b},{c},{d}]
   */
  if (restOfTheAccounts.length !== 0) {
    restOfTheAccounts.sort((a: any, b: any) =>
      a.clientId.localeCompare(b.clientId),
    );
  }

  /** step-3: join lead account array and restOfTheAccounts array,
   * e.g. if [{a},{c}] are lead account then join with rest of the account [{b},{d}]
   * final list will be [a},{c},{b},{d}] after concatenating
   */
  if (leadAccounts.length !== 0) {
    finalAccountList = leadAccounts.concat(restOfTheAccounts);
  } else {
    // no lead account was found
    finalAccountList = [];
  }

  return finalAccountList;
};

export const setImagePath = (imageName: string): string =>
  /* sets full svg only image url for the asset from control center, separate image name (*-desk) for desktop
   imageName has to match with the asset name uploaded in control center
   sample name: 'account-not-found' */
  isMobile
    ? imageName && `${CONTROL_CENTER_IMAGE_PATH}/${imageName}.svg`
    : imageName && `${CONTROL_CENTER_IMAGE_PATH}/${imageName}-desk.svg`;

export const getRedirectRouteOnError = (
  context: string,
  status: string | number,
  // code?: string - code is businessErrors[0].code from redux
): string => {
  // code is businessErrors:[{ code: '1002', message: 'test message' }]
  // context is the name of api i.e. session, accounts, refresh, logout
  let redirectLink = '';
  switch (status.toString()) {
    case '1003':
    case '1004':
      redirectLink = '/session-timeout-error';
      break;
    case '1005':
    case '400':
      redirectLink = '/link-not-valid';
      break;
    case '422':
      if (context === 'accounts') {
        redirectLink = '/account-not-found';
      } else {
        redirectLink = '/session-timeout-error';
      }
      break;
    case '401':
    case '501':
    case '503':
      redirectLink = '/not-verified';
      break;
    case '500':
      redirectLink = '/system-down';
      break;
    default:
      redirectLink = '/link-not-valid';
      break;
  }
  return redirectLink;
};

export const setErrorInAppState = (
  error: any,
  apiName: string,
  navigateToErrorPage = true, // navigate to error pages by default
): void => {
  const api: any = apiName.split('/').pop();
  if (!isEmpty(error)) {
    let errorObject;
    if (error.status) {
      errorObject = {
        status: error.status,
        message: error.statusText,
        navigateToErrorPage,
      };
    } else {
      errorObject = {
        status: '',
        message: error.message,
        navigateToErrorPage,
      };
    }
    if (error.data?.businessErrors && error.data.businessErrors.length > 0) {
      errorObject = { ...errorObject, ...error.data.businessErrors[0] };
    }
    store.dispatch(setError({ [api]: errorObject }));
  }
};

export const formatAmount = (num: number | string): string => {
  const amount = typeof num === 'string' ? parseFloat(num) : num;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const splitTokenNumber = (accountNumberString: string) => {
  if (!accountNumberString) return [];
  // returns this format -> ["1234", "5678", "9876", "5432"]
  return accountNumberString.match(/.{4}/g);
};

export const convertStringToLowerCase = (string: string): string => {
  if (string) {
    return string.toLowerCase();
  } else {
    return '';
  }
};

export const convertStringToUpperCase = (string: string): string => {
  if (string) {
    return string.toUpperCase();
  } else {
    return '';
  }
};

export const getAuthTokenFromHTMLViaNode = (html = null): string => {
  const doc = html ?? document;
  const inputTokenElement: HTMLInputElement = doc.getElementById(
    'authToken',
  ) as HTMLInputElement;
  if (inputTokenElement) {
    const authToken: string = inputTokenElement.value;
    return authToken;
  }
  console.log('Cannot find the authToken value.');
  return '';
};

export const paymentTokenBusinessErrorCheck = (response: any) => {
  let maxAttemptsStatusCheck = false;
  let accountNotActivatedCheck = false;

  if (response?.status === 422 && response?.data?.businessErrors?.length > 0) {
    const errorCode = response.data.businessErrors[0].code;
    switch (errorCode) {
      case '1009':
        maxAttemptsStatusCheck = true;
        break;
      case '1002':
        accountNotActivatedCheck = true;
        break;
    }
  }
  return { maxAttemptsStatusCheck, accountNotActivatedCheck };
};

export const initiateSplunkRum = () => {
  window?.SplunkRum?.init({
    beaconUrl: 'https://rum-ingest.us0.signalfx.com/v1/rum',
    rumAuth: VITE_SPLUNK_RUM_AUTH,
    app: 'wallet-ui',
    environment: VITE_SPLUNK_RUM_ENV,
  });
  window?.SplunkSessionRecorder?.init({
    beaconUrl: 'https://rum-ingest.us0.signalfx.com/v1/rumreplay',
    rumAuth: VITE_SPLUNK_RUM_AUTH,
  });
};

export const handleTabAction = (event: any, action: any) => {
  if (event.key === 'Enter') {
    event.preventDefault();
    action(event);
  }
};

export const setD2dDeliveryMethod = (value: string) => {
  switch (value) {
    case 'MERCHANT':
      return 'qr';
    case 'TEXT':
      return 'sms';
    case 'EMAIL':
      return 'el';
    default:
      return '';
  }
};

export const validateInput = ({
  name,
  value,
  errors,
  creditLimit = '0.00',
}: validateInputParams): string => {
  const TEN_THOUSAND = 10000;
  // make sure credit limit is number before comparison is performed
  const creditLimitNumber = parseFloat(creditLimit.replace(/,/g, ''));
  const MINIMUM_DESIRED_LIMIT = creditLimitNumber + 50;

  if (!value.trim()) {
    // if empty return required field text
    return errors.requiredErrorText;
  }
  if (/^\d*$/.test(value) && parseInt(value) === 0) {
    // if invalid input value return respective error
    return errors.valueIsZeroErrorTexts[name];
  }
  if (name === 'desiredAmount' && parseInt(value) < MINIMUM_DESIRED_LIMIT) {
    // if invalid input value return respective error
    return errors.valueIsInvalidErrorTexts[name];
  }
  if (name === 'annualNetIncome' && parseInt(value) < TEN_THOUSAND) {
    // if invalid input value return respective error
    return errors.valueIsInvalidErrorTexts[name];
  }
  // return default empty state if input is valid
  return '';
};
